// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  FiCheckCircle as CheckCircle, FiAlertTriangle as AlertTriangle, FiShield as Shield,
  FiFileText as FileText, FiDatabase as Database, FiSettings as Settings,
  FiArrowRight as ArrowRight, FiLoader as Loader2, FiRefreshCw as RefreshCw,
  FiPlus as Plus, FiTrash2 as Trash2, FiEdit2 as Edit2,
  FiCheck as Check, FiX as X, FiAlertCircle as AlertCircle, FiTarget as Target,
  FiChevronDown as ChevronDown, FiChevronUp as ChevronUp
} from 'react-icons/fi';
import {
  getPromotionPolicies, createPromotionPolicy, updatePromotionPolicy, deletePromotionPolicy
} from '../../../../../services/aiOperationsApi';
import { listDatasets } from '../../../../../services/datasetsApi';

export const MODEL_OPTIONS = {
  openai: [
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
  ],
  anthropic: [
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
    { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
    { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' }
  ],
  bedrock: [
    { value: 'anthropic.claude-3-sonnet-20240229-v1:0', label: 'Claude 3 Sonnet (Bedrock)' },
    { value: 'anthropic.claude-3-haiku-20240307-v1:0', label: 'Claude 3 Haiku (Bedrock)' },
    { value: 'amazon.titan-text-express-v1', label: 'Titan Text Express' }
  ]
};

export const TRIGGER_OPTIONS = [
  { value: 'always', label: 'Always (all promotions)' },
  { value: 'staging_only', label: 'Staging Only' },
  { value: 'production_only', label: 'Production Only' }
];

export const SCORING_OPTIONS = [
  { value: 'exact_match', label: 'Exact Match' },
  { value: 'contains', label: 'Contains' },
  { value: 'similarity', label: 'Similarity Score' },
  { value: 'llm_judge', label: 'LLM Judge' }
];

export const POLICY_TEMPLATES = [
  {
    id: 'strict',
    name: 'Strict',
    description: 'High quality bar for production-critical prompts',
    color: 'red',
    config: {
      minPassRate: 0.9,
      minAvgScore: 0.85,
      passThreshold: 0.8,
      trigger: 'production_only'
    }
  },
  {
    id: 'standard',
    name: 'Standard',
    description: 'Balanced quality gate for most use cases',
    color: 'blue',
    config: {
      minPassRate: 0.8,
      minAvgScore: 0.7,
      passThreshold: 0.7,
      trigger: 'always'
    }
  },
  {
    id: 'lenient',
    name: 'Lenient',
    description: 'Lower bar for experimental or low-risk prompts',
    color: 'green',
    config: {
      minPassRate: 0.7,
      minAvgScore: 0.6,
      passThreshold: 0.6,
      trigger: 'staging_only'
    }
  }
];

export const TriggerBadge = ({ trigger }) => {
  const config = {
    always: { label: 'All Promotions', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: RefreshCw },
    staging_only: { label: 'Staging Only', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Shield },
    production_only: { label: 'Production Only', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: Shield }
  };
  const { label, color, icon: Icon } = config[trigger] || config.always;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const PolicyModal = ({ isOpen, onClose, onSave, policy, datasets }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    promptName: '*',
    trigger: 'always',
    datasetId: '',
    minPassRate: 0.8,
    minAvgScore: 0.7,
    maxAvgLatencyMs: '',
    maxTotalCost: '',
    provider: 'openai',
    modelId: 'gpt-4o-mini',
    scoringMethod: 'contains',
    passThreshold: 0.7,
    isActive: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (policy) {
      setFormData({
        name: policy.name || '',
        description: policy.description || '',
        promptName: policy.prompt_name || '*',
        trigger: policy.trigger || 'always',
        datasetId: policy.dataset_id || '',
        minPassRate: policy.quality_gate_config?.min_pass_rate || 0.8,
        minAvgScore: policy.quality_gate_config?.min_avg_score || 0.7,
        maxAvgLatencyMs: policy.quality_gate_config?.max_avg_latency_ms || '',
        maxTotalCost: policy.quality_gate_config?.max_total_cost || '',
        provider: policy.evaluation_config?.provider || 'openai',
        modelId: policy.evaluation_config?.model_id || 'gpt-4o-mini',
        scoringMethod: policy.evaluation_config?.scoring_method || 'contains',
        passThreshold: policy.evaluation_config?.pass_threshold || 0.7,
        isActive: policy.is_active !== false
      });
    } else {
      setFormData({
        name: '',
        description: '',
        promptName: '*',
        trigger: 'always',
        datasetId: '',
        minPassRate: 0.8,
        minAvgScore: 0.7,
        maxAvgLatencyMs: '',
        maxTotalCost: '',
        provider: 'openai',
        modelId: 'gpt-4o-mini',
        scoringMethod: 'contains',
        passThreshold: 0.7,
        isActive: true
      });
    }
  }, [policy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        prompt_name: formData.promptName,
        trigger: formData.trigger,
        dataset_id: formData.datasetId || null,
        is_active: formData.isActive,
        quality_gate_config: {
          min_pass_rate: Number.parseFloat(formData.minPassRate),
          min_avg_score: Number.parseFloat(formData.minAvgScore),
          max_avg_latency_ms: formData.maxAvgLatencyMs ? Number.parseInt(formData.maxAvgLatencyMs) : null,
          max_total_cost: formData.maxTotalCost ? Number.parseFloat(formData.maxTotalCost) : null
        },
        evaluation_config: {
          provider: formData.provider,
          model_id: formData.modelId,
          scoring_method: formData.scoringMethod,
          pass_threshold: Number.parseFloat(formData.passThreshold)
        }
      };

      await onSave(payload);
      onClose();
    } catch (err) {
      alert(err.message || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {policy ? 'Edit Policy' : 'Create Promotion Policy'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {!policy && (
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Start from Template</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Choose a preset or customize from scratch</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {POLICY_TEMPLATES.map(template => {
                  const colorClasses = {
                    red: 'border-red-300 dark:border-red-700 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20',
                    blue: 'border-blue-300 dark:border-blue-700 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20',
                    green: 'border-green-300 dark:border-green-700 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                  };
                  const badgeColors = {
                    red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                    green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  };
                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        name: prev.name || `${template.name} Quality Gate`,
                        minPassRate: template.config.minPassRate,
                        minAvgScore: template.config.minAvgScore,
                        passThreshold: template.config.passThreshold,
                        trigger: template.config.trigger
                      }))}
                      className={`p-3 border-2 rounded-lg text-left transition-colors ${colorClasses[template.color]}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900 dark:text-white">{template.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${badgeColors[template.color]}`}>
                          {(template.config.minPassRate * 100).toFixed(0)}%
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{template.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Basic Information</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Define what this policy applies to and when it triggers</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="policy-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Policy Name *</label>
                <input
                  id="policy-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Production Quality Gate"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                  title="A descriptive name for this policy"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="policy-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  id="policy-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Ensures all production prompts pass 80% of tests before deployment"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  title="Optional description to help team members understand this policy"
                />
              </div>
              <div>
                <label htmlFor="policy-prompt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Applies To Prompt</label>
                <input
                  id="policy-prompt"
                  type="text"
                  value={formData.promptName}
                  onChange={(e) => setFormData({ ...formData, promptName: e.target.value })}
                  placeholder="* for all prompts"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Use * to apply to all prompts, or enter a specific prompt name"
                />
                <p className="text-xs text-gray-500 mt-1">Use <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">*</code> for all prompts, or enter a specific prompt name to target only that prompt</p>
              </div>
              <div>
                <label htmlFor="policy-trigger" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">When to Trigger</label>
                <select
                  id="policy-trigger"
                  value={formData.trigger}
                  onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Choose when this policy should be enforced"
                >
                  {TRIGGER_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.trigger === 'always' && 'Policy runs on every promotion (dev→staging, staging→prod)'}
                  {formData.trigger === 'staging_only' && 'Policy runs only when promoting to staging'}
                  {formData.trigger === 'production_only' && 'Policy runs only when promoting to production'}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Test Dataset</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Select a dataset with test cases to evaluate prompts against</p>
            </div>
            <div>
              <label htmlFor="policy-eval-dataset" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Evaluation Dataset</label>
              <select
                id="policy-eval-dataset"
                value={formData.datasetId}
                onChange={(e) => setFormData({ ...formData, datasetId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                title="The dataset containing test cases for evaluation"
              >
                <option value="">Select a dataset...</option>
                {datasets.map(ds => (
                  <option key={ds.id} value={ds.id}>{ds.name} ({ds.test_case_count || 0} cases)</option>
                ))}
              </select>
              {datasets.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> No datasets available. Create a dataset first in the Datasets section.
                </p>
              )}
              {formData.datasetId && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Prompts will be evaluated against this dataset during promotion
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Quality Gate Thresholds</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Set minimum requirements that must be met for promotion to succeed</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="policy-min-pass-rate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Pass Rate *</label>
                <input
                  id="policy-min-pass-rate"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={formData.minPassRate}
                  onChange={(e) => setFormData({ ...formData, minPassRate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Minimum percentage of test cases that must pass"
                />
                <p className="text-xs text-gray-500 mt-1">Value 0-1 (e.g., 0.8 = 80% of tests must pass)</p>
              </div>
              <div>
                <label htmlFor="policy-min-avg-score" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Avg Score *</label>
                <input
                  id="policy-min-avg-score"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={formData.minAvgScore}
                  onChange={(e) => setFormData({ ...formData, minAvgScore: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Minimum average score across all test cases"
                />
                <p className="text-xs text-gray-500 mt-1">Value 0-1 (e.g., 0.7 = average score must be at least 70%)</p>
              </div>
              <div>
                <label htmlFor="policy-max-latency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Avg Latency (ms)</label>
                <input
                  id="policy-max-latency"
                  type="number"
                  min="0"
                  value={formData.maxAvgLatencyMs}
                  onChange={(e) => setFormData({ ...formData, maxAvgLatencyMs: e.target.value })}
                  placeholder="Optional - e.g., 5000"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Maximum average response time in milliseconds (optional)"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: fail if average response time exceeds this</p>
              </div>
              <div>
                <label htmlFor="policy-max-cost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Total Cost ($)</label>
                <input
                  id="policy-max-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.maxTotalCost}
                  onChange={(e) => setFormData({ ...formData, maxTotalCost: e.target.value })}
                  placeholder="Optional - e.g., 1.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Maximum total cost for the evaluation run (optional)"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: fail if total LLM cost exceeds this amount</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Evaluation Configuration</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Configure the LLM used to judge prompt responses against expected outputs</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="policy-llm-provider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">LLM Provider</label>
                <select
                  id="policy-llm-provider"
                  value={formData.provider}
                  onChange={(e) => {
                    const newProvider = e.target.value;
                    const defaultModel = MODEL_OPTIONS[newProvider]?.[0]?.value || '';
                    setFormData({ ...formData, provider: newProvider, modelId: defaultModel });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="The LLM provider used to evaluate responses"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="bedrock">AWS Bedrock</option>
                </select>
              </div>
              <div>
                <label htmlFor="policy-model" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                <select
                  id="policy-model"
                  value={formData.modelId}
                  onChange={(e) => setFormData({ ...formData, modelId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="The specific model to use for evaluation"
                >
                  {(MODEL_OPTIONS[formData.provider] || []).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Smaller models are faster and cheaper; larger models may be more accurate</p>
              </div>
              <div>
                <label htmlFor="policy-scoring-method" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scoring Method</label>
                <select
                  id="policy-scoring-method"
                  value={formData.scoringMethod}
                  onChange={(e) => setFormData({ ...formData, scoringMethod: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="How responses are compared to expected outputs"
                >
                  {SCORING_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.scoringMethod === 'exact_match' && 'Response must exactly match expected output'}
                  {formData.scoringMethod === 'contains' && 'Response must contain the expected output'}
                  {formData.scoringMethod === 'similarity' && 'Semantic similarity score between response and expected'}
                  {formData.scoringMethod === 'llm_judge' && 'LLM evaluates if response meets criteria'}
                </p>
              </div>
              <div>
                <label htmlFor="policy-pass-threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pass Threshold</label>
                <input
                  id="policy-pass-threshold"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={formData.passThreshold}
                  onChange={(e) => setFormData({ ...formData, passThreshold: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Score threshold for a test case to be considered passed"
                />
                <p className="text-xs text-gray-500 mt-1">Value 0-1: Individual test case passes if score is above this</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div>
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Policy is active
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">When disabled, this policy will not be enforced during promotions</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {policy ? 'Update Policy' : 'Create Policy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PoliciesContent = () => {
  const [policies, setPolicies] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [expandedPolicy, setExpandedPolicy] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [policiesRes, datasetsRes] = await Promise.all([getPromotionPolicies(), listDatasets({ limit: 100 })]);
        setPolicies(policiesRes.data || []);
        setDatasets(datasetsRes.data || datasetsRes || []);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    loadData();
  }, []);

  const handleDeletePolicy = async (policyId, policyName) => {
    if (!globalThis.confirm(`Delete "${policyName}"?`)) return;
    try { await deletePromotionPolicy(policyId); setPolicies(prev => prev.filter(p => p.id !== policyId)); } catch (err) { alert(err.message); }
  };

  const handleToggleActive = async (policy) => {
    try {
      await updatePromotionPolicy(policy.id, { isActive: !policy.is_active });
      setPolicies(prev => prev.map(p => p.id === policy.id ? { ...p, is_active: !p.is_active } : p));
    } catch (err) { alert(err.message); }
  };

  const handleCreatePolicy = async (payload) => {
    const result = await createPromotionPolicy(payload);
    setPolicies(prev => [...prev, result]);
  };

  const handleUpdatePolicy = async (payload) => {
    if (!editingPolicy) return;
    const result = await updatePromotionPolicy(editingPolicy.id, payload);
    setPolicies(prev => prev.map(p => p.id === editingPolicy.id ? { ...p, ...result } : p));
    setEditingPolicy(null);
  };

  const formatPercentage = (value) => value == null ? 'N/A' : `${(value * 100).toFixed(0)}%`;
  const getDatasetInfo = (datasetId) => datasets.find(d => d.id === datasetId);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;

  return (
    <div>
      <PolicyModal
        isOpen={showCreateModal || editingPolicy !== null}
        onClose={() => { setShowCreateModal(false); setEditingPolicy(null); }}
        onSave={editingPolicy ? handleUpdatePolicy : handleCreatePolicy}
        policy={editingPolicy}
        datasets={datasets}
      />

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 mb-6 border border-blue-100 dark:border-blue-800">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Promotion Policies
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
              Policies automatically evaluate prompts against test datasets before they can be promoted to staging or production.
              Set quality thresholds to ensure only high-quality prompts reach your users.
            </p>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap shadow-sm">
            <Plus className="w-5 h-5" /> Create Policy
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">How it works</p>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-gray-700 dark:text-gray-300">Promote Prompt</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
              <Target className="w-4 h-4 text-purple-600" />
              <span className="text-gray-700 dark:text-gray-300">Policy Check</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
              <Database className="w-4 h-4 text-green-600" />
              <span className="text-gray-700 dark:text-gray-300">Run Evaluation</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span className="text-gray-700 dark:text-gray-300">Quality Gate</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
              <Shield className="w-4 h-4 text-amber-600" />
              <span className="text-gray-700 dark:text-gray-300">Approval</span>
            </div>
          </div>
        </div>
      </div>

      {policies.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Promotion Policies Yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create your first policy to ensure consistent quality when promoting prompts between environments.
            </p>

            <div className="text-left bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 mb-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">Quick Start</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">1</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Create a policy</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Define which prompts and environments it applies to</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">2</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Set quality thresholds</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Minimum pass rate and average score required</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">3</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Link a test dataset</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Your prompts will be evaluated against this dataset</p>
                  </div>
                </div>
              </div>
            </div>

            <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm">
              <Plus className="w-5 h-5" /> Create Your First Policy
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
          {policies.map((policy) => {
            const datasetInfo = getDatasetInfo(policy.dataset_id);
            return (
              <div key={policy.id}>
                <div className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => setExpandedPolicy(expandedPolicy === policy.id ? null : policy.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpandedPolicy(expandedPolicy === policy.id ? null : policy.id); }} role="button" tabIndex={0}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {expandedPolicy === policy.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className={`w-2 h-2 rounded-full ${policy.is_active ? 'bg-green-500' : 'bg-gray-400'}`} title={policy.is_active ? 'Active' : 'Inactive'} />
                          <span className="font-medium text-gray-900 dark:text-white">{policy.name}</span>
                          <TriggerBadge trigger={policy.trigger} />
                          {!policy.is_active && <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">Disabled</span>}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span>Applies to: <span className="text-gray-700 dark:text-gray-300">{policy.prompt_name === '*' ? 'All prompts' : policy.prompt_name}</span></span>
                          {datasetInfo && (
                            <span className="flex items-center gap-1">
                              <Database className="w-3 h-3" />
                              {datasetInfo.name}
                              {datasetInfo.test_case_count && <span className="text-xs">({datasetInfo.test_case_count} tests)</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="hidden sm:block text-right text-sm">
                        <p className="text-gray-500 dark:text-gray-400">Quality Gate</p>
                        <p className="font-medium text-gray-900 dark:text-white">{formatPercentage(policy.quality_gate_config?.min_pass_rate)} pass rate</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); handleToggleActive(policy); }} className={`p-2 rounded-lg transition-colors ${policy.is_active ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`} title={policy.is_active ? 'Disable policy' : 'Enable policy'}>
                          {policy.is_active ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setEditingPolicy(policy); }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title="Edit policy">
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeletePolicy(policy.id, policy.name); }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="Delete policy">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {expandedPolicy === policy.id && (
                  <div className="px-6 pb-4 bg-gray-50 dark:bg-gray-900/50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 mb-3">
                          <Target className="w-5 h-5 text-blue-600" />
                          <h4 className="font-medium text-gray-900 dark:text-white">Quality Gate</h4>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Minimum thresholds for promotion</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Min Pass Rate</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatPercentage(policy.quality_gate_config?.min_pass_rate)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Min Avg Score</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatPercentage(policy.quality_gate_config?.min_avg_score)}</span>
                          </div>
                          {policy.quality_gate_config?.max_avg_latency_ms && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Max Latency</span>
                              <span className="font-medium text-gray-900 dark:text-white">{policy.quality_gate_config.max_avg_latency_ms}ms</span>
                            </div>
                          )}
                          {policy.quality_gate_config?.max_total_cost && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Max Cost</span>
                              <span className="font-medium text-gray-900 dark:text-white">${policy.quality_gate_config.max_total_cost}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 mb-3">
                          <Settings className="w-5 h-5 text-green-600" />
                          <h4 className="font-medium text-gray-900 dark:text-white">Evaluation Config</h4>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">LLM used to evaluate responses</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Provider</span>
                            <span className="font-medium text-gray-900 dark:text-white capitalize">{policy.evaluation_config?.provider || 'openai'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Model</span>
                            <span className="font-medium text-gray-900 dark:text-white text-xs">{policy.evaluation_config?.model_id || 'gpt-4o-mini'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Scoring</span>
                            <span className="font-medium text-gray-900 dark:text-white capitalize">{(policy.evaluation_config?.scoring_method || 'contains').replace('_', ' ')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 mb-3">
                          <Database className="w-5 h-5 text-purple-600" />
                          <h4 className="font-medium text-gray-900 dark:text-white">Test Dataset</h4>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Dataset used for evaluation</p>
                        {datasetInfo ? (
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Name</span>
                              <span className="font-medium text-gray-900 dark:text-white">{datasetInfo.name}</span>
                            </div>
                            {datasetInfo.test_case_count && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Test Cases</span>
                                <span className="font-medium text-gray-900 dark:text-white">{datasetInfo.test_case_count}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            No dataset configured
                          </div>
                        )}
                      </div>
                    </div>
                    {policy.description && (
                      <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Description</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{policy.description}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PoliciesContent;
